import React, { useState } from 'react';
import css from 'styled-jsx/css';
import { formatter } from '@yupwego/string-fns';

import classnames from 'classnames';
import { colors } from '../common/Theme';

import { EditIcon, TrashIcon } from '../common/icons/icons';
import Button from '../common/elements/Button.js';
import List from '../common/elements/List.js';
import Pricer from '@yupwego/pricer';

const beneficiaryQuoteStyles = css`
  .details-text {
    margin: 15px 0;
  }

  .details {
    padding-top: 0;
    padding-bottom: 0;
    background-color: ${colors.ywgPaleGrey};
    border-top: 1px solid ${colors.ywgVeryLightPink};
    border-bottom-right-radius: 10px;
  }

  .pricing {
    display: flex;
    flex-wrap: wrap;
  }
  .pricing > :nth-child(even) {
    border-top: 1px solid ${colors.ywgVeryLightPink};
  }

  .pricing__item {
    padding: 0 15px;
    box-sizing: border-box;
  }

  @media (min-width: 900px) {
    .pricing > :nth-child(even) {
      border-left: 1px solid ${colors.ywgVeryLightPink};
      border-top: none;
    }
    .pricing__item {
      padding: 0 50px;
    }
  }
`;

const quoteStyles = css`
  .quote {
    border: 1px solid ${colors.ywgVeryLightPink};
    border-radius: 10px 0 10px 0;
  }
  .beneficiary-summary {
    display: flex;
    padding: 10px;
    align-items: center;
    cursor: pointer;
  }
  .beneficiaries__description {
    flex-grow: 1;
  }
  .beneficiary-name {
    color: ${colors.ywgPrimary};
    font-size: 1.2rem;
    font-weight: 700;
  }
  .beneficiary-price,
  .beneficiary-birthday {
    font-size: 0.7rem;
  }
  .beneficiary-price {
    display: block;
    margin-bottom: 5px;
  }
  .details-button {
    margin-left: 10px;
    background-color: transparent;
    border: none;
    outline: none;
    transition: transform 0.3s ease;
    border-radius: 0;
  }
  .details-button:hover {
    transform: translate3d(0, 3px, 0);
  }
  .details-button--active:hover {
    transform: translate3d(0, -3px, 0);
  }
  .details-carret {
    stroke: ${colors.ywgNavy};
  }
  .details-button:focus .details-carret,
  .details-button:hover .details-carret {
    stroke: ${colors.ywgPinkishOrange};
  }
  .details-button:active .details-carret {
    stroke: ${colors.ywgPinkishOrangeStrong};
  }

  @media (min-width: 900px) {
    .beneficiary-price {
      display: inline-block;
      margin-left: 5px;
      margin-bottom: 0;
    }
    .beneficiary-price:before {
      content: '(';
    }
    .beneficiary-price:after {
      content: ')';
    }
  }
`;

const listStyles = css.resolve`
  .list {
    margin-bottom: 20px;
  }
`;

const actionStyles = css.resolve`
  .action {
    border: none;
    background-color: #e8ebef;
    border-radius: 50%;
    height: 50px;
    width: 50px;
    margin: 0 0 0 10px;
    padding: 0;
  }
`;
const promotionsText = {
  accompanying_children: {
    explanation:
      "Votre enfant étant accompagné d'au moins une autre personne (exemple un parent) bénéficie d'une tarification adaptée jusqu'à ses 20 ans. Les enfants de moins de 20 ans séjournant seuls, ne bénéficient pas de ce tarif."
  }
};
const BeneficiaryQuote = ({ beneficiaryQuote, travel, prices }) => {
  const days = beneficiaryQuote.details.days;
  const areaPrices = Object.entries(prices.zones).reduce(
    (acc, [key, value]) => {
      const areaPrice = Pricer.selectPriceRange(
        beneficiaryQuote.details.age,
        value,
        value
      ).price;
      const areaDetails = {
        text: `Zone ${key} : ${areaPrice.toFixed(2)} € / jour (${(
          7 * areaPrice
        ).toFixed(2)} € / semaine)`,
        key: `quote-pricing-zone-${key.toLowerCase()}`
      };
      return [...acc, areaDetails];
    },
    []
  );
  const promotionItems = () => {
    return Object.entries(beneficiaryQuote.promotion.zones).reduce(
      (acc, [area, priceRanges]) => {
        return [
          ...acc,
          {
            key: `quote-promotion-${beneficiaryQuote.promotion.name}-${area}`,
            text: `Zone ${area} : ${priceRanges[0].price} € / jour (${priceRanges[0].price} € / semaine / bénéficiaire)`
          }
        ];
      },
      []
    );
  };

  return (
    <div className="details pricing">
      {beneficiaryQuote.promotion ? (
        <div className="pricing__item">
          {promotionsText[beneficiaryQuote.promotion.name].explanation ? (
            <p className="details-text">
              {promotionsText[beneficiaryQuote.promotion.name].explanation}
            </p>
          ) : null}
          <List
            className={listStyles.className}
            items={promotionItems()}
          ></List>
        </div>
      ) : (
        <div className="pricing__item">
          {travel.endDate ? (
            <p className="details-text">
              {beneficiaryQuote.price.toFixed(2)} € pour {days} jour(s).
            </p>
          ) : days > 1 ? (
            <p className="details-text">
              {beneficiaryQuote.price.toFixed(2)} € pour les {days} premiers
              jours.
            </p>
          ) : (
            <p className="details-text">
              {beneficiaryQuote.price.toFixed(2)} € pour le premier jour.
            </p>
          )}
          {travel.endDate ? null : (
            <>
              <p>Puis tous les 7 (sept) jours: </p>
              <List className={listStyles.className} items={areaPrices}></List>
            </>
          )}
        </div>
      )}
      <style jsx>{beneficiaryQuoteStyles}</style>
    </div>
  );
};

const Quote = ({
  quotation,
  quotationBeneficiary,
  travel,
  removeBeneficiary,
  editBeneficiary,
  prices
}) => {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const toggleDetails = () => {
    setDetailsVisible(!detailsVisible);
  };

  return (
    <>
      <div className="quote">
        <div
          className="beneficiary-summary"
          onClick={() => setDetailsVisible(!detailsVisible)}
        >
          <div className="beneficiaries__description">
            <div className="beneficiary-name">
              {quotationBeneficiary.beneficiary.firstname}{' '}
              {quotationBeneficiary.beneficiary.lastname}
              <span className="beneficiary-price">
                Zone {quotation.zone} - {quotationBeneficiary.price.toFixed(2)}
                &nbsp;€ / semaine
              </span>
            </div>
            <div className="beneficiary-birthday">
              {formatter(quotationBeneficiary.beneficiary.birthday)}
            </div>
          </div>
          <Button
            title={`edit ${quotationBeneficiary.beneficiary.firstname} ${quotationBeneficiary.beneficiary.lastname}`}
            className={classnames('action', actionStyles.className)}
            id={quotationBeneficiary.beneficiary.key}
            onClick={() => editBeneficiary(quotationBeneficiary.beneficiary)}
            secondary
          >
            <EditIcon />
          </Button>
          <Button
            title={`remove ${quotationBeneficiary.beneficiary.firstname} ${quotationBeneficiary.beneficiary.lastname}`}
            className={classnames('action', actionStyles.className)}
            id={quotationBeneficiary.beneficiary.key}
            onClick={() => removeBeneficiary(quotationBeneficiary.beneficiary)}
            secondary
          >
            <TrashIcon />
          </Button>
          <button
            className={classnames(
              'action',
              'details-button',
              {
                'details-button--active': detailsVisible
              },
              actionStyles.className
            )}
            onClick={toggleDetails}
          >
            <svg
              className="details-carret"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="square"
              strokeLinejoin="arcs"
            >
              {detailsVisible ? (
                <path d="M18 15l-6-6-6 6" />
              ) : (
                <path d="M6 9l6 6 6-6" />
              )}
            </svg>
          </button>
        </div>
        {detailsVisible ? (
          <BeneficiaryQuote
            travel={travel}
            beneficiaryQuote={quotationBeneficiary}
            prices={prices}
          ></BeneficiaryQuote>
        ) : null}
      </div>

      {actionStyles.styles}
      {listStyles.styles}
      <style jsx>{quoteStyles}</style>
    </>
  );
};

export default Quote;
